import { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import holdingsLogo from "../assets/images/holdings.svg";
import Select from "../components/Select";
import Input from "../components/input";
import {
  fetchTransporterCitiesDropdown,
  fetchTransporterVehicleTypesDropdown,
} from "../store/Slices/dropdownSlice";
import "../styles/Issue.css";
import "../styles/RFQRequest.css";
import { successNotify } from "../utils/toast";

const safeArray = (value) => (Array.isArray(value) ? value : []);
const cityOptions = [
  { label: "Islamabad", value: "islamabad" },
  { label: "Rawalpindi", value: "rawalpindi" },
  { label: "Lahore", value: "lahore" },
  { label: "Karachi", value: "karachi" },
  { label: "Faisalabad", value: "faisalabad" },
  { label: "Multan", value: "multan" },
  { label: "Peshawar", value: "peshawar" },
  { label: "Sialkot", value: "sialkot" },
];
const vehicleTypeOptions = [
  { label: "Light", value: 1 },
  { label: "Heavy", value: 2 },
  { label: "Trailers", value: 3 },
];

const toLabel = (item) =>
  String(
    item?.label ?? item?.name ?? item?.title ?? item?.city ?? item?.value ?? ""
  ).trim();

const TRANSPORTER_BY_LOAD_SCHEMA = Yup.object().shape({
  vehicleTypes: Yup.array()
    .of(Yup.mixed())
    .min(1, "At least one vehicle type is required"),
  transporterBranch: Yup.string().trim().required("Transporter branch is required"),
  price: Yup.number()
    .typeError("Price must be a number")
    .min(0, "Price must be at least 0")
    .required("Price is required"),
  date: Yup.date().required("Date is required"),

});

const initialValues = {
  vehicleTypes: [],
  transporterBranch: "",
  price: "",
  date: "",
  load_id: "",
};

const pickSingleOption = (options, selectedValue) => {
  const selected = String(selectedValue || "").trim().toLowerCase();
  return (
    safeArray(options).find((option) => {
      const optionValue = String(option?.value ?? "").trim().toLowerCase();
      const optionLabel = String(option?.label ?? "").trim().toLowerCase();
      return optionValue === selected || optionLabel === selected;
    }) || null
  );
};

const pickMultiOptions = (options, selectedValues) => {
  const selectedSet = new Set(
    safeArray(selectedValues).map((value) => String(value).trim().toLowerCase())
  );
  return safeArray(options).filter((option) => {
    const optionValue = String(option?.value ?? "").trim().toLowerCase();
    const optionLabel = String(option?.label ?? "").trim().toLowerCase();
    return selectedSet.has(optionValue) || selectedSet.has(optionLabel);
  });
};

const TransporterByLoad = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    transporterCitiesDropdownData = [],
    transporterVehicleTypesDropdownData = [],
  } = useSelector((state) => state.dropdowns || {});

  useEffect(() => {
    dispatch(fetchTransporterCitiesDropdown());
    dispatch(fetchTransporterVehicleTypesDropdown());
  }, [dispatch]);

  const transporterVehicleTypeOptions = useMemo(() => {
    const source = safeArray(transporterVehicleTypesDropdownData).length
      ? transporterVehicleTypesDropdownData
      : vehicleTypeOptions;

    return safeArray(source)
      .map((item) => {
        const label = toLabel(item);
        if (!label) return null;
        const value = item?.value ?? item?.id ?? item?.vehicle_type_id ?? label;
        return { label, value };
      })
      .filter(Boolean);
  }, [transporterVehicleTypesDropdownData]);

  const transporterBranchOptions = useMemo(() => {
    const source = safeArray(transporterCitiesDropdownData).length
      ? transporterCitiesDropdownData
      : cityOptions;

    return safeArray(source)
      .map((item) => {
        const label = toLabel(item);
        if (!label) return null;
        const value = item?.value ?? item?.city ?? item?.name ?? label;
        return { label, value };
      })
      .filter(Boolean);
  }, [transporterCitiesDropdownData]);

  return (
    <div
      className="rfq-request-bg"
      style={{ backgroundColor: "#f4f7fe", minHeight: "100vh", padding: "20px 0" }}
    >
      <div
        className="rfq-request-page"
        style={{ maxWidth: "767px", margin: "0 auto", padding: "0 15px" }}
      >
        <div
          className="card shadow-sm border-0 mb-4 rfq-request-card"
          style={{ borderRadius: "16px", overflow: "hidden" }}
        >
          <div className="card-header bg-white py-3 px-4 border-bottom-0 rfq-request-card-header">
            <h5 className="mb-0" style={{ fontWeight: "700", color: "#333" }}>
              Transporter By Load
            </h5>
          </div>
          <div className="card-body p-4 p-md-5 pt-0 rfq-request-card-body">
            <Formik
              initialValues={initialValues}
              validationSchema={TRANSPORTER_BY_LOAD_SCHEMA}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                successNotify("Transporter quotation submitted successfully");
                resetForm();
                setSubmitting(false);
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
                isValid,
                setFieldValue,
                setFieldTouched,
              }) => (
                <Form>
                  <div className="d-flex justify-content-between align-items-start mb-4 rfq-top-info">
                    <div>
                      <h5
                        style={{
                          fontWeight: "bold",
                          fontSize: "1.2rem",
                          color: "#333",
                          marginBottom: "10px",
                        }}
                      >
                        <span style={{ color: "#0d6efd" }}>Hello</span> Transporter
                      </h5>
                      <p className="text-muted mb-1" style={{ fontSize: "0.9rem" }}>
                        Please submit your transporter quotation against selected load.
                      </p>
                    </div>
                    <div>
                      <img
                        src={holdingsLogo}
                        alt="J HOLDINGS"
                        className="rfq-top-logo"
                        style={{ height: "70px", objectFit: "contain" }}
                      />
                    </div>
                  </div>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Select
                        label="Transporter Vehicle Types"
                        placeholder="Select Vehicle Types"
                        options={transporterVehicleTypeOptions}
                        value={pickMultiOptions(
                          transporterVehicleTypeOptions,
                          values.vehicleTypes
                        )}
                        onChange={(selectedOptions) =>
                          setFieldValue(
                            "vehicleTypes",
                            safeArray(selectedOptions).map((option) => option?.value)
                          )
                        }
                        onBlur={() => setFieldTouched("vehicleTypes", true)}
                        isMulti
                        error={errors.vehicleTypes}
                        touched={touched.vehicleTypes}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Select
                        label="Transporter Branch"
                        placeholder="Select Branch"
                        options={transporterBranchOptions}
                        value={pickSingleOption(
                          transporterBranchOptions,
                          values.transporterBranch
                        )}
                        onChange={(selectedOption) =>
                          setFieldValue(
                            "transporterBranch",
                            selectedOption?.value || ""
                          )
                        }
                        onBlur={() => setFieldTouched("transporterBranch", true)}
                        error={errors.transporterBranch}
                        touched={touched.transporterBranch}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Input
                        label="Price"
                        type="number"
                        name="price"
                        placeholder="Enter Price"
                        value={values.price}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.price}
                        touched={touched.price}
                      />
                    </Col>
                    <Col md={6}>
                      <Input
                        label="Date"
                        type="date"
                        name="date"
                        value={values.date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.date}
                        touched={touched.date}
                      />
                    </Col>
                  </Row>



                  <div className="d-flex gap-3 justify-content-end rfq-form-actions">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="rfq-btn-secondary"
                      style={{
                        border: "1px solid #1447B1",
                        color: "#1447B1",
                        backgroundColor: "#fff",
                        padding: "8px 30px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        height: "42px",
                        minWidth: "100px",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !isValid}
                      className="rfq-btn-primary"
                      style={{
                        backgroundColor: "#1447B1",
                        color: "#fff",
                        border: "none",
                        padding: "8px 24px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        flex: 1,
                        maxWidth: "80%",
                        height: "42px",
                        opacity: isSubmitting ? 0.7 : 1,
                        cursor: isSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransporterByLoad;
